/**
 * Utilities for handling Hardhat artifacts and extracting Sourcify-compatible metadata
 */

export interface HardhatArtifact {
  contractName: string;
  abi: any[];
  bytecode: string;
  deployedBytecode: string;
  metadata: string; // Stringified JSON!
}

export interface SourceifyMetadata {
  compiler: {
    version: string;
  };
  language: string;
  settings: {
    compilationTarget: Record<string, string>;
    optimizer: {
      enabled: boolean;
      runs: number;
    };
  };
  sources: Record<string, {
    keccak256: string;
    license?: string;
    urls?: string[];
  }>;
}

/**
 * Check if a file is a Hardhat artifact
 */
export function isHardhatArtifact(fileContent: any): boolean {
  return (
    typeof fileContent === 'object' &&
    fileContent !== null &&
    'contractName' in fileContent &&
    'abi' in fileContent &&
    'metadata' in fileContent &&
    typeof fileContent.metadata === 'string'
  );
}

/**
 * Check if a file is already Sourcify-compatible metadata
 */
export function isSourceifyMetadata(fileContent: any): boolean {
  return (
    typeof fileContent === 'object' &&
    fileContent !== null &&
    'language' in fileContent &&
    (fileContent.language === 'Solidity' || fileContent.language === 'Vyper') &&
    'compiler' in fileContent &&
    'settings' in fileContent &&
    'sources' in fileContent
  );
}

/**
 * Extract Sourcify metadata from Hardhat artifact
 */
export function extractMetadataFromHardhatArtifact(
  artifact: HardhatArtifact
): SourceifyMetadata {
  try {
    const metadata = JSON.parse(artifact.metadata);

    if (!isSourceifyMetadata(metadata)) {
      throw new Error('Invalid metadata structure in Hardhat artifact');
    }

    return metadata;
  } catch (error) {
    throw new Error(
      `Failed to parse metadata from Hardhat artifact: ${error instanceof Error ? error.message : 'Unknown error'}`
    );
  }
}

/**
 * Process uploaded files to extract metadata and sources
 * Handles both raw metadata.json and Hardhat artifacts
 */
export async function processUploadedFiles(files: File[]): Promise<{
  metadata: SourceifyMetadata | null;
  sources: File[];
  hardhatArtifactDetected: boolean;
}> {
  let metadata: SourceifyMetadata | null = null;
  const sources: File[] = [];
  let hardhatArtifactDetected = false;

  for (const file of files) {
    // Skip non-JSON and non-SOL files
    if (!file.name.endsWith('.json') && !file.name.endsWith('.sol')) {
      continue;
    }

    // Handle Solidity source files
    if (file.name.endsWith('.sol')) {
      sources.push(file);
      continue;
    }

    // Parse JSON files
    try {
      const content = await file.text();
      const parsed = JSON.parse(content);

      // Check if it's a Hardhat artifact
      if (isHardhatArtifact(parsed)) {
        hardhatArtifactDetected = true;
        if (!metadata) {
          metadata = extractMetadataFromHardhatArtifact(parsed);
        }
        continue;
      }

      // Check if it's already Sourcify metadata
      if (isSourceifyMetadata(parsed)) {
        if (!metadata) {
          metadata = parsed;
        }
        continue;
      }
    } catch (error) {
      // Ignore files that can't be parsed as JSON
      console.warn(`Skipping file ${file.name}: ${error}`);
    }
  }

  return { metadata, sources, hardhatArtifactDetected };
}

/**
 * Create a new File object from metadata object
 */
export function createMetadataFile(metadata: SourceifyMetadata): File {
  const blob = new Blob([JSON.stringify(metadata, null, 2)], {
    type: 'application/json',
  });
  return new File([blob], 'metadata.json', { type: 'application/json' });
}
